import { FunctionComponent, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Flex, InputGroup, Text, useToast } from '@chakra-ui/react';
import { ModalFormData } from '../PostsEditModal/schemas';
import { ImagePreview } from '@/components/ImagePreview';
import { CircleFileUpload } from '@/components/CircleFileUpload';
import { EditMediaModal } from '@/components/EditMediaModal';
import { PostStatus } from '@/model';
import { useTranslation } from 'react-i18next';
import { compressImage } from '@/utils/image';

interface MediaFieldProps {
  isEdit?: boolean;
}

export const MediaField: FunctionComponent<MediaFieldProps> = ({ isEdit = false }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const form = useFormContext<ModalFormData>();
  const formValues = form.watch();
  const uploaderRef = useRef<HTMLInputElement | null>(null);

  const [editMediaModalOpen, setEditMediaModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingImageFile, setEditingImageFile] = useState<File | undefined>(undefined);

  const isEditable = !isEdit || formValues.status === PostStatus.DRAFT;

  const fetchImageAsFile = async (url: string, filename: string): Promise<File> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  };

  return (
    <>
      <Flex direction="column" gap={2}>
        <InputGroup display="flex" flexDirection="column" gap={1}>
          <Text fontWeight="medium">
            {t('pages.application_posts.components.media_field.media_field_add_media_label')}
          </Text>
        </InputGroup>

        <Box bg="gray.100" p={4} borderRadius={4} display="flex" flexWrap="wrap" gap={4}>
          {formValues.posts_media?.map((media, index) => (
            <ImagePreview
              key={index}
              src={
                media.file_cropped || media.file
                  ? URL.createObjectURL(media.file_cropped ?? media.file)
                  : media.image_url
              }
              onActionClick={
                isEditable
                  ? async () => {
                      let fileToEdit = media.file;
                      if (!fileToEdit && media.image_url) {
                        // Só busca o arquivo se necessário
                        fileToEdit = await fetchImageAsFile(media.image_url, `media-${index}.png`);
                      }
                      setEditingIndex(index);
                      setEditingImageFile(fileToEdit);
                      setEditMediaModalOpen(true);
                    }
                  : undefined
              }
              onDeleteClick={
                isEditable
                  ? () => {
                      const updated = formValues.posts_media?.filter((_, i) => i !== index);
                      form.setValue('posts_media', updated);
                    }
                  : undefined
              }
            />
          ))}

          <CircleFileUpload
            ref={(el) => (uploaderRef.current = el)}
            disabled={(formValues.posts_media?.length ?? 0) >= 5 || !isEditable}
            accept=".png, .jpg, .jpeg, .mp4"
            onFileChange={async (file) => {
              form.clearErrors();
              if (uploaderRef.current) uploaderRef.current.value = '';

              if (!file) return;

              const processedFile = await compressImage(file);

              const hasItem = formValues.posts_media?.find(
                (mediaFile) => mediaFile?.file?.name === processedFile.name
              );
              if (hasItem) {
                toast({
                  title: t('pages.application_posts.components.media_field.media_field_file_already_selected_warning'),
                  status: 'warning',
                });
                return;
              }

              form.setValue(`posts_media.${formValues.posts_media?.length ?? 0}`, { file: processedFile });
            }}
          />
        </Box>

        <Text fontSize="12px">
          {t('pages.application_posts.components.media_field.media_field_max_files_helper')}
        </Text>

        {form.formState.errors.posts_media && (
          <Text fontSize="14px" color="red.500">
            {form.formState.errors.posts_media.message}
          </Text>
        )}
      </Flex>

      <EditMediaModal
        isOpen={editMediaModalOpen}
        aspect="POSTS"
        image={editingImageFile}
        onClose={() => setEditMediaModalOpen(false)}
        onSave={async (newFile) => {
          if (editingIndex === null) return;

          const compressedFile = await compressImage(newFile);

          // Remove o antigo
          const updated = formValues.posts_media?.filter((_, i) => i !== editingIndex) ?? [];
          // Adiciona o novo como "novo"
          form.setValue('posts_media', [...updated, { file: compressedFile }]);

          setEditMediaModalOpen(false);
        }}
      />
    </>
  );
};
